import React, { useEffect, useRef, useState } from "react";
import { Catalog, Diagnostic } from "../../powerFlow/language";
import {
  monaco,
  ensureGridRulesLanguage,
  setDiagnostics,
  setEditorContext,
  clearEditorContext,
  GRID_RULES_LANGUAGE,
  GRID_RULES_THEME,
} from "../editor/gridRulesLanguage";

interface Props {
  /** The committed rule text — what the model is currently built from. */
  source: string;
  /** Called with the edited text when the edit is applied (blur or ⌘/Ctrl+Enter). */
  onChange: (source: string) => void;
  /**
   * The problems with a draft, located in its text — squiggles as you type, and
   * the list under the editor. Called with the committed text too, when the
   * draft still matches it, so there's one answer rather than two that can drift.
   */
  diagnose: (draft: string) => Diagnostic[];
  /** The relations this rule is checked against — what completions offer. */
  catalog: Catalog;
  /**
   * Open a relation's panel, as clicking it in the tree would — with the column
   * the jump came from, when it came from one (`from` in `l.from`).
   */
  onNavigate?: (relation: string, column?: string) => void;
  /** A disabled rule stays editable but is greyed out — it's not in the model. */
  disabled?: boolean;
  /** Extra note above the editor, e.g. which variable class a bounds rule declares. */
  label?: string;
  /**
   * Show the rule but don't let it be edited — for the built-in ACPF rules,
   * which are source to read (still highlighted and navigable), not to change.
   */
  readOnly?: boolean;
}

const wrapperStyle: React.CSSProperties = {
  padding: "6px 8px",
  background: "#fbfbf6",
  borderBottom: "1px solid #eee",
};

const hintStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "#999",
  marginTop: "3px",
};

const errorStyle: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "11px",
  color: "#c62828",
  marginTop: "4px",
  whiteSpace: "pre-wrap",
};

const LINE_HEIGHT = 18;
const PADDING = 8;

/** Model URIs have to be unique, and two panels can hold the same rule's id. */
let modelSeq = 0;

/**
 * The editor for one symbolic rule — the quantified math that generates a class
 * of constraints (or declares a class of variables), shown above the grounded
 * rows it produces.
 *
 * Edits are held locally and applied on blur (or ⌘/Ctrl+Enter), not on every
 * keystroke: each applied edit re-grounds and re-solves the whole model, and a
 * half-typed rule doesn't parse. The draft is still checked live, so errors are
 * underlined as you type — they just don't reach the model until you're done.
 *
 * The editor is Monaco, taught the rule language in `editor/gridRulesLanguage`:
 * the relation names and columns it completes, the errors it underlines and the
 * definitions it jumps to are all the compiler's, not a second description of
 * the language kept in step by hand.
 */
const RuleEditor: React.FC<Props> = ({
  source,
  onChange,
  diagnose,
  catalog,
  onNavigate,
  disabled,
  label,
  readOnly,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [draft, setDraft] = useState(source);
  const [height, setHeight] = useState(LINE_HEIGHT * 2 + PADDING);

  // The editor is created once and lives across renders, so what its callbacks
  // need has to be read at call time rather than captured at creation.
  const latest = useRef({ source, onChange, diagnose, catalog, onNavigate });
  latest.current = { source, onChange, diagnose, catalog, onNavigate };

  useEffect(() => {
    ensureGridRulesLanguage();
    const container = containerRef.current;
    if (!container) return;

    const uri = monaco.Uri.parse(`inmemory://grid-rules/${modelSeq++}.rule`);
    const model = monaco.editor.createModel(latest.current.source, GRID_RULES_LANGUAGE, uri);
    const editor = monaco.editor.create(container, {
      model,
      theme: GRID_RULES_THEME,
      automaticLayout: true,
      // The built-in ACPF rules are shown to read, not edit — highlighted and
      // navigable, but locked.
      readOnly: !!readOnly,
      domReadOnly: !!readOnly,
      fontSize: 12,
      fontFamily: "monospace",
      lineHeight: LINE_HEIGHT,
      lineNumbers: "off",
      glyphMargin: false,
      folding: false,
      lineDecorationsWidth: 4,
      lineNumbersMinChars: 0,
      minimap: { enabled: false },
      overviewRulerLanes: 0,
      overviewRulerBorder: false,
      hideCursorInOverviewRuler: true,
      renderLineHighlight: "none",
      // Rainbow brackets are a lot of colour for one line of math.
      bracketPairColorization: { enabled: false },
      scrollBeyondLastLine: false,
      // The panel is narrow and the rules are long: wrapping keeps the whole
      // rule on screen instead of hiding half of it behind a scrollbar.
      wordWrap: "on",
      wrappingIndent: "indent",
      guides: { indentation: false },
      // The panel this sits in does the scrolling; the editor grows to fit.
      scrollbar: { vertical: "hidden", horizontal: "auto", alwaysConsumeMouseWheel: false },
      padding: { top: 4, bottom: 4 },
      tabSize: 2,
      contextmenu: false,
      // The rules' vocabulary is the catalog's, not the other words on screen.
      wordBasedSuggestions: "off",
      quickSuggestions: { other: true, comments: false, strings: false },
      // The panel clips its overflow, which would cut the suggest widget in half.
      fixedOverflowWidgets: true,
    });
    editorRef.current = editor;

    setEditorContext(uri.toString(), {
      catalog: latest.current.catalog,
      diagnose: (text) => latest.current.diagnose(text),
      navigate: (relation, column) => latest.current.onNavigate?.(relation, column),
    });

    const apply = () => {
      const text = editor.getValue();
      if (text !== latest.current.source) latest.current.onChange(text);
    };

    const disposables = [
      model.onDidChangeContent(() => setDraft(editor.getValue())),
      // Grow to fit the rule, so there's never an inner scrollbar on two lines
      // of math.
      editor.onDidContentSizeChange(() => setHeight(Math.max(editor.getContentHeight(), LINE_HEIGHT + PADDING))),
      editor.onDidBlurEditorWidget(apply),
    ];

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, apply);
    // Escape reverts — but only when it isn't already closing a widget, which is
    // what the user means by it while the suggestions are open.
    editor.addCommand(
      monaco.KeyCode.Escape,
      () => {
        editor.setValue(latest.current.source);
        // Focus lives on the editor's hidden textarea, not on the container.
        (document.activeElement as HTMLElement | null)?.blur();
      },
      "!suggestWidgetVisible && !findWidgetVisible && !parameterHintsVisible",
    );

    return () => {
      disposables.forEach((d) => d.dispose());
      clearEditorContext(uri.toString());
      editorRef.current = null;
      editor.dispose();
      model.dispose();
    };
  }, []);

  // Follow the committed text when it changes underneath us (undo, the
  // final-SOC toggle rewriting its rule, a scenario load) — but not while the
  // user is typing in this editor, which would fight their edit.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.hasTextFocus()) return;
    if (editor.getValue() !== source) editor.setValue(source);
    setDraft(source);
  }, [source]);

  const dirty = draft !== source;
  const diagnostics = diagnose(draft);
  const messages = diagnostics.map((d) => d.message);

  // Underline what's wrong with the text as it stands. Keyed on the messages and
  // their spans, so this is a no-op for the renders that change neither.
  const markerKey = JSON.stringify(diagnostics.map((d) => [d.message, d.span]));
  useEffect(() => {
    const model = editorRef.current?.getModel();
    if (model) setDiagnostics(model, diagnostics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerKey]);

  return (
    <div style={wrapperStyle}>
      {label && (
        <div style={{ fontSize: "11px", color: "#888", marginBottom: "3px" }}>{label}</div>
      )}
      <div
        ref={containerRef}
        style={{
          height: `${height}px`,
          opacity: disabled ? 0.55 : 1,
          border: `1px solid ${messages.length > 0 ? "#e57373" : dirty ? "#90caf9" : "#ddd"}`,
          borderRadius: "3px",
          overflow: "hidden",
          background: "white",
        }}
      />
      {messages.length > 0 && (
        <div style={errorStyle}>{messages.map((message) => `⚠ ${message}`).join("\n")}</div>
      )}
      {messages.length === 0 && dirty && (
        <div style={hintStyle}>Unapplied edit — blur the field or press ⌘/Ctrl+Enter to apply.</div>
      )}
      {messages.length === 0 && !dirty && disabled && (
        <div style={hintStyle}>Disabled — this rule is not part of the model.</div>
      )}
    </div>
  );
};

export default RuleEditor;
